/**
 * resizeObserver — deliver every resize callback on the NEXT frame, never inside the observation.
 *
 * A ResizeObserver callback that changes layout resizes the element being observed, and the browser
 * cannot settle that within one frame: it defers the remaining notifications and raises
 * "ResizeObserver loop completed with undelivered notifications" as a window error. **Nothing is
 * broken when it fires** — the deferred notifications arrive on the next frame — but the dev
 * server's overlay treats any window error as fatal and throws a red screen over a working app.
 * Deleting a cell is one routine way to get there: a modal opens and closes, the body's scrollbar
 * goes and comes back, and everything on the page changes width twice in quick succession.
 *
 * Three observers in this app already defer by hand for exactly this reason — the workspace grid
 * (`workspace/grid/index.js`), the Ace views (`widgets/common/aceView.js`) and the narrow-feed
 * toggle (`widgets/common/narrowWidth.js`), each with the failure written out in its own comment.
 * A LIBRARY's observer gets no such treatment, and ReactFlow, AG Grid and Ace all run one. Applying
 * the rule once at the boundary is what extends it to them; for our own three it changes nothing,
 * since a deferred callback that defers again still runs on the next frame.
 *
 * **It is not error suppression, and deliberately not.** The obvious alternative — filter that one
 * message out of `window.onerror` — cannot work here anyway: `react-error-overlay` registers its
 * listener from a dev entry that runs before this app's, so a later listener cannot
 * `stopImmediatePropagation` an earlier one. And it would leave the loop in place. This removes the
 * cause instead, and a real error from inside a resize callback still reaches the overlay: it is
 * thrown from an animation-frame callback rather than from the observer's, which the overlay
 * reports the same way.
 *
 * The cost is one frame of latency on a resize, which is what our own observers already pay.
 */

/**
 * Replace `window.ResizeObserver` with one that batches its callbacks onto an animation frame.
 * Idempotent, and a no-op where the API is absent (jsdom without a polyfill, an old browser).
 *
 * @returns {() => void} restores the original, for a test that wants the raw one back
 */
export function installDeferredResizeObserver() {
  const Native = typeof window !== 'undefined' ? window.ResizeObserver : undefined;
  if (!Native || Native.__deferred) return () => {};

  class DeferredResizeObserver extends Native {
    constructor(callback) {
      let frame = null;
      let batch = [];
      super((entries, observer) => {
        // Every entry is kept, not just the newest: one delivery can carry several elements, and a
        // caller that reads `entries` expects to see each element it observed. Coalescing happens
        // at the FRAME — a burst inside one frame runs the callback once, with everything in it.
        batch.push(...entries);
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          const delivered = batch;
          batch = [];
          callback(delivered, observer);
        });
      });
    }
  }
  DeferredResizeObserver.__deferred = true;

  window.ResizeObserver = DeferredResizeObserver;
  return () => { window.ResizeObserver = Native; };
}
